import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import { ArrowLeft, Layers, Share2, Trash2, X } from "lucide-react";
import {
  deleteCollection,
  formatNumber,
  getCollection,
  removeVideoFromCollection,
} from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import usePageMeta from "@/hooks/use-page-meta";
import usePageJsonLd, { secondsToISODuration } from "@/hooks/use-page-jsonld";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function CollectionDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const key = slug ? `/collections/${slug}` : null;
  const { data, isLoading, error } = useSWR(key, () => getCollection(slug));

  const [editing, setEditing] = useState(false);

  usePageMeta({
    title: data ? `${data.title} — ${data.count} videos` : "Collection",
    description: data
      ? data.description || `${data.count} videos in the "${data.title}" shelf on the tobiasdeamon Vault.`
      : undefined,
    image: data?.videos?.[0]?.thumbnail,
    type: "website",
  });

  // CollectionPage + ItemList JSON-LD — helps Google understand shelves as
  // discrete listings of videos.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  usePageJsonLd({
    key: "collection",
    data:
      data && origin
        ? {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: data.title,
            description:
              data.description ||
              `${data.count} videos in the "${data.title}" shelf on the tobiasdeamon Vault.`,
            url: `${origin}/collections/${data.slug}`,
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: data.count,
              itemListElement: (data.videos || []).slice(0, 50).map((vid, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${origin}/video/${vid.id}`,
                item: {
                  "@type": "VideoObject",
                  name: vid.title,
                  thumbnailUrl:
                    vid.thumbnail || `https://i.ytimg.com/vi/${vid.id}/hqdefault.jpg`,
                  uploadDate: vid.published_at,
                  duration: secondsToISODuration(vid.duration_seconds),
                  contentUrl: `https://www.youtube.com/watch?v=${vid.id}`,
                  embedUrl: `https://www.youtube.com/embed/${vid.id}`,
                },
              })),
            },
          }
        : null,
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <Skeleton className="h-6 w-40 bg-white/5" />
        <Skeleton className="h-12 w-2/3 bg-white/5" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto py-24 text-center">
        <div className="mono-label text-white/40">404</div>
        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">
          Collection not found.
        </h1>
        <Link
          to="/collections"
          className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full border border-white/20 hover:border-[#d95d39] hover:text-[#d95d39] font-mono-tech text-xs uppercase tracking-[0.22em]"
        >
          <ArrowLeft className="w-4 h-4" /> All collections
        </Link>
      </div>
    );
  }

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: data.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      }
    } catch {
      /* user cancelled */
    }
  };

  const handleRemove = async (video_id) => {
    try {
      const updated = await removeVideoFromCollection(slug, video_id);
      await mutate(key, updated, false);
      toast.message("Removed from collection");
    } catch {
      toast.error("Couldn't remove video");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCollection(slug);
      await mutate("/collections");
      toast.success("Collection deleted");
      navigate("/collections");
    } catch {
      toast.error("Couldn't delete collection");
    }
  };

  const videos = data.videos || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="collection-detail">
      <Link
        to="/collections"
        className="inline-flex items-center gap-2 mono-label text-white/50 hover:text-[#d95d39] transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> All collections
      </Link>

      <div className="flex items-start justify-between gap-4 sm:gap-6 flex-wrap">
        <div className="max-w-3xl min-w-0">
          <div className="mono-label inline-flex items-center gap-2">
            <Layers className="w-3.5 h-3.5" /> Collection · {formatNumber(data.count)} videos
          </div>
          <h1
            data-testid="collection-title"
            className="mt-3 font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] break-words"
          >
            {data.title}
          </h1>
          {data.description && (
            <p className="mt-4 text-white/60 text-base sm:text-lg leading-relaxed">{data.description}</p>
          )}
        </div>
        <div className="w-full sm:w-auto flex flex-wrap items-center gap-2">
          <Button
            onClick={handleShare}
            data-testid="collection-share-btn"
            className="rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-[11px] uppercase tracking-[0.22em] px-5"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <Button
            onClick={() => setEditing((v) => !v)}
            variant="outline"
            data-testid="collection-edit-toggle"
            className="rounded-full bg-transparent border-white/15 text-white hover:border-[#d95d39] hover:text-[#d95d39] font-mono-tech text-[11px] uppercase tracking-[0.22em]"
          >
            {editing ? "Done" : "Edit"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="collection-delete-btn"
                className="rounded-full bg-transparent border-white/15 text-white/70 hover:border-red-500 hover:text-red-400 font-mono-tech text-[11px] uppercase tracking-[0.22em]"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#12141d] border-white/10 text-[#f4f1ea]">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif-editorial text-2xl">
                  Delete this collection?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-white/60">
                  The videos stay in the archive. Only this named collection is removed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-white/15 text-white/70 hover:bg-white/5">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  data-testid="collection-delete-confirm"
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="mt-12">
        {videos.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 bg-[#0c0e15] p-16 text-center">
            <p className="font-serif-editorial text-3xl text-[#f4f1ea]">Empty collection.</p>
            <p className="text-white/50 mt-2">
              Open any video from the archive and tap "Save to collection" to add it here.
            </p>
            <Link
              to="/archive"
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-[#d95d39] hover:bg-[#c14e2e] text-white font-mono-tech text-xs uppercase tracking-[0.22em]"
            >
              Browse the archive
            </Link>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="collection-video-grid"
          >
            {videos.map((v) => (
              <div key={v.id} className="relative group">
                <VideoCard video={v} />
                {editing && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleRemove(v.id);
                    }}
                    data-testid={`remove-from-collection-${v.id}`}
                    className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/80 border border-white/10 text-white/80 hover:bg-red-500 hover:border-red-500 hover:text-white flex items-center justify-center transition-colors"
                    aria-label="Remove from collection"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
